import React, { useState, useEffect, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { Button, Table, Space, Card } from 'antd';
import * as actions from '@actions';
import type { ColumnsType } from 'antd';
import styled from '@emotion/styled';
import { useSessionAppointment } from '@hooks/useSessionAppointment';

const StyledGPRecord = styled.div`
  font-size: 0.9em;

  .patient-consent {
    width: 100%;
    text-align: center;
    margin-bottom: 10px;
    font-style: italic;
  }

  .patient-card {
    margin-bottom: 20px;
    font-weight: 600;
    border: 1px solid var(--productPrimary);

    small {
      font-weight: normal;
    }

    .ant-card-head {
      color: var(--productPrimary);
      font-weight: 600;

      .ant-card-extra {
        color: var(--productPrimary);
        font-weight: 600;
        small {
          margin-left: 20px;
        }
      }
    }

    .ant-card-grid {
      box-shadow: none;
      padding: 10px;
    }
  }

  .tab {
    box-shadow: none;
    border-radius: 0px;
  }

  .ant-tabs-left > .ant-tabs-nav .ant-tabs-tab {
    padding: 8px 20px 0px 0px;
    font-style: italic;
    font-size: 0.9em;
    font-weight: 600;
  }

  h1 {
    display: none;
  }

  h2 {
    font-size: 18px;
    color: black;
    font-weight: 600;
  }

  table {
    width: 100%;
    text-align: left;
    border-radius: 2px 2px 0 0;
    border-collapse: separate;
    border-spacing: 0;
    border-color: grey;
    margin-bottom: 30px;
  }

  tbody > tr > td,
  thead > tr > th,
  tfoot > tr > td,
  tfoot > tr > th {
    padding: 12px 8px;
  }

  thead > tr > th {
    color: rgba(0, 0, 0, 0.85);
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
  }

  tbody > tr > td {
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.3s;
  }

  .gptransfer-banner {
    color: red;
  }
`;

const ERSreferral = ({ className = '' }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { appointment } = useSessionAppointment();
  const classNames = cx('clinician-gprecord', className);
  const [ErsReferralAttachments, setErsReferralAttachments] = useState([]);
  const [ErsPatientDetails, setErsPatientDetails] = useState({ nhs_number: undefined });
  const [activeUBRN, setActiveUBRN] = useState('');
  const [showContents, setShowContents] = useState(false);

  const columns: ColumnsType<any> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <div>{text}</div>,
      onCell: () => ({ 'data-testid': 'title-cell' }) as HTMLAttributes<any>
    },
    {
      title: 'Date Added',
      dataIndex: 'date_added',
      key: 'date_added',
      render: (text) => <div>{text}</div>,
      onCell: () => ({ 'data-testid': 'date-added-cell' }) as HTMLAttributes<any>
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Space size="middle">
          <Button type="primary" onClick={() => getDocumentPdf(record.att_lid)} data-testid="download-attachment">
            {t('Open')}
          </Button>
        </Space>
      ),
      onCell: () => ({ 'data-testid': 'action-cell' }) as HTMLAttributes<any>
    }
  ];

  // generate tab titles/keys from a list of ubrns
  function tab_generator(ubrns) {
    ubrns = Array.from(new Set(ubrns));
    const tabs = [];
    let i = 1;
    try {
      ubrns.forEach((ubrn) => {
        if (ubrn) {
          tabs.push({
            key: ubrn,
            tab: `UBRN #${i.toString()}`
          });
          i += 1;
        }
      });
    } catch {
      // empty
    }
    return tabs;
  }

  // generates the actual tab labels in a card
  const ubrn_tabs = () => {
    const tab_names = tab_generator(appointment.ubrn);

    return (
      <Card
        style={{ width: '100%' }}
        tabList={tab_names}
        // makes it so no tab is selected to start
        activeTabKey=""
        onTabChange={(key) => {
          setActiveUBRN(key);
          setShowContents(true);
        }}
      />
    );
  };

  async function getInformationPdf() {
    const res = await dispatch(actions.getErsReferralInformation(appointment.appointment_id, activeUBRN));

    if (res?.data) {
      const buff = Buffer.from(res.data, 'base64');
      const array = new Uint8Array(buff);
      const file = new Blob([array], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } else {
      console.log('ERS call failed');
    }
  }

  async function getDocumentPdf(att_lid) {
    const res = await dispatch(actions.getErsReferralDocument(appointment.appointment_id, activeUBRN, att_lid));

    if (res?.data) {
      const buff = Buffer.from(res.data, 'base64');
      const array = new Uint8Array(buff);
      const file = new Blob([array], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } else {
      console.log('ERS call failed');
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (activeUBRN) {
        const res = await dispatch(actions.getErsReferralAttachments(appointment.appointment_id, activeUBRN));
        try {
          if (res[0]?.att_lid) {
            setErsReferralAttachments(res);
          }
        } catch {
          // empty
        }
      }
    };
    fetchData();
  }, [appointment?.appointment_id, activeUBRN, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (activeUBRN) {
        const res = await dispatch(actions.getErsPatientDetails(appointment.appointment_id, activeUBRN));
        if (res?.nhs_number) {
          setErsPatientDetails(res);
        }
      }
    };
    fetchData();
  }, [appointment?.appointment_id, activeUBRN, dispatch]);

  return (
    <div>
      <div>{ubrn_tabs()}</div>
      {showContents && (
        <StyledGPRecord className={classNames}>
          <Card
            className="patient-card"
            size="small"
            title={`UBRN : ${activeUBRN}`}
            extra={
              <>
                <small>{t('Gender : ')}</small>
                {t('TBD')}
                <small>{t('DOB : ')}</small>
                {t('TBD')}
                <small>{t('Age : ')}</small>
                {t('TBD')}
                <small>{t('NHS No. : ')}</small>
                {ErsPatientDetails.nhs_number}
              </>
            }
          >
            <Card.Grid hoverable={false} style={{ width: '45%' }}>
              <small>{t('Address : ')}</small> {t(' TBD')}
            </Card.Grid>
            <Card.Grid hoverable={false} style={{ width: '25%' }}>
              <small>{t('Phone : ')}</small> {t(' TBD')}
            </Card.Grid>
            <Card.Grid hoverable={false} style={{ width: '30%' }}>
              <small>
                <Button type="primary" onClick={() => getInformationPdf()} data-testid="get-information-pdf">
                  {t('Clinical information')}
                </Button>
              </small>
            </Card.Grid>
          </Card>

          <Table columns={columns} dataSource={ErsReferralAttachments} />
        </StyledGPRecord>
      )}
    </div>
  );
};

export default ERSreferral;
